import { SnackbarKey } from 'notistack';
import { atom } from 'recoil';
import {
    BackendClient,
    BlobUploadType,
    RequestParameters,
} from '~/backend/backend-client';
import {
    TaskStatus as ConnectorTaskStatus,
    TaskOutput,
} from '~/backend/connector-api/connector-schemas';
import { GenerationModelDetails } from '~/domain/dicom/dicomBase';
import { DoseTargetData } from '~/utils/transforms/doseTargetsTransforms';
import { PredictionModel } from '~/utils/transforms/predictionModelsDataTransforms';
import { WorkflowTypes } from './viewerAtoms';

export type TaskStatus =
    | ConnectorTaskStatus
    | 'Queued'
    | 'Uploading'
    | 'Downloading';

export const FINISHED_JOB_STATUSES: TaskStatus[] = [
    'Done',
    'Error',
    'Canceled',
];

export type TaskStateBase = {
    model?: PredictionModel | GenerationModelDetails;
    output?: TaskOutput;
};

export type AutoSegmentationTaskState = TaskStateBase & {
    subSelectedStructures?: string[];
    includeStructureSet?: boolean;
};

export type DosePredictionTaskState = TaskStateBase & {
    targets?: DoseTargetData[];
};

export type DataUploadTaskState = TaskStateBase & {
    itemsAmount?: number;
};

export interface JobPayload {
    filesToUpload: BlobUploadType[];
    backendParameters: RequestParameters;
    pseudonymized: boolean;
}

export type TaskState =
    | TaskStateBase
    | AutoSegmentationTaskState
    | DosePredictionTaskState
    | DataUploadTaskState;

export type Job = {
    id: string;
    name: string;
    type: WorkflowTypes;
    status: TaskStatus;
    onLoad?: (job: Job) => Promise<void>;
    payload?: JobPayload;
    client?: BackendClient;
    task?: TaskState;
    results?: File[];
    error?: string;
    isImported?: boolean;
    uploadedItems?: number;
    totalItems?: number;
    createdAt: number;
    updatedAt?: number;
    finishedAt?: number;
};

export type JobQueue = {
    snackbarId?: SnackbarKey;
    jobs: Job[];
};

export const jobQueueState = atom<JobQueue>({
    key: 'jobQueueState',
    default: {
        snackbarId: undefined,
        jobs: [],
    },
    dangerouslyAllowMutability: true,
});

export const jobQueueExpandedState = atom<boolean>({
    key: 'jobQueueExpandedState',
    default: true,
});

export const isJobRunning = (status: TaskStatus): boolean => {
    return !FINISHED_JOB_STATUSES.includes(status);
};
